import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';

import CardOutline from '../CardOutline';
import TextInput from '../TextInput';
import LoaderComponent from '@evdy/web-core/dist/components/shared/elements/LoaderComponent';

import { renderToast } from '@evdy/web-core/dist/lib/utils';

import { webCommunityOperations } from '@evdy/web-redux/dist/lib/webCommunity';

import desktopLoveIcon from '../../public/images/icons/desktop_love.svg';

import styles from './GetLinkCard.module.scss';

const { getInviteLink, resetInviteLink } = webCommunityOperations;

const {
  'get-link-card': getLinkCardClass,
  borderless: borderlessClass,
  'outline-card': cardOutlineClass,
  'default-header-img': defaultCardHeaderImgClass,
  'get-link-button': getLinkBtnClass,
  'email-form': emailFormClass
} = styles;

const copy = {
  guest: {
    header: 'View this on your computer',
    body: 'We’ll email you this link so you can easily see the details from your desktop or tablet.'
  },
  loggedIn: {
    header: 'Want to view this on your computer?',
    body: 'We’ll email you this link so you can easily see the details from your desktop or tablet.'
  }
};

const GetLinkCard = ({ borderless }) => {
  const dispatch = useDispatch();
  const { userData, memorialId, inviteLink } = useSelector(({ authUser, webCommunity }) => ({
    userData: authUser.data,
    memorialId: webCommunity.inviteMeta.data?._id,
    inviteLink: webCommunity.inviteLink
  }));
  const { _id: userId, email, shellEmail, meta: { posterShellEmail } = {} } = userData ?? {};
  const userEmail = email || shellEmail || posterShellEmail;
  const {
    success: sentLinkSuccess,
    error: { message: errorMessage } = {},
    isFetching
  } = inviteLink;
  const handleSubmitEmail = ({ email }) => {
    dispatch(getInviteLink({ userId, memorialId, email: userEmail || email }));
  };

  // show toast on success
  useEffect(() => {
    if (sentLinkSuccess) {
      const toastCopy = 'Thank you, we have sent you an email with a link to this Everdays page';
      renderToast({ id: 'get-desktop-link-success', message: toastCopy });
      dispatch(resetInviteLink());
    }
    if (errorMessage) {
      renderToast({
        id: 'get-desktop-link-error',
        message: errorMessage || '',
        type: 'error'
      });
    }
  }, [errorMessage, sentLinkSuccess]);

  const renderSubmitButton = () => {
    return (
      <button className={getLinkBtnClass} onClick={handleSubmitEmail}>
        Email link
      </button>
    );
  };

  const renderEmailInput = () => {
    const emailRegex = /^\S+@\S+\.\S+$/;
    const EmailFormSchema = Yup.object().shape({
      email: Yup.string().matches(emailRegex, 'Please provide a valid email')
    });

    return (
      <Formik
        initialValues={{ email: '' }}
        validationSchema={EmailFormSchema}
        onSubmit={handleSubmitEmail}
      >
        <Form className={emailFormClass}>
          <TextInput type="email" name="email" placeholder="Enter your email" />
          <button type="submit">Submit</button>
        </Form>
      </Formik>
    );
  };

  const getLinkCardClasses = classNames(getLinkCardClass, { [borderlessClass]: borderless });

  return (
    <div className={getLinkCardClasses}>
      <CardOutline customClass={cardOutlineClass}>
        <div className={defaultCardHeaderImgClass}>
          <img src={desktopLoveIcon} alt="view on desktop" />
        </div>
        <h1>{copy[!!userEmail ? 'loggedIn' : 'guest'].header}</h1>
        <p>{copy[!!userEmail ? 'loggedIn' : 'guest'].body}</p>
        {!!userEmail ? renderSubmitButton() : renderEmailInput()}
      </CardOutline>
      {isFetching && <LoaderComponent />}
    </div>
  );
};

export default GetLinkCard;
